import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { adminUpsertArticle, listCategories, adminGetArticle, adminPublishArticle } from "@/lib/articles.functions";
import { listAuthorsPublic } from "@/lib/authors.functions";
import { adminGenerateDraftFromInput } from "@/lib/editorial.functions";
import { toast } from "sonner";
import { formatDate } from "@/lib/site";

export const Route = createFileRoute("/admin/new")({
  component: () => <ArticleEditor />,
});

type FaqItem = { question: string; answer: string };

type FormState = {
  title: string;
  slug: string;
  subtitle: string;
  excerpt: string;
  content_md: string;
  cover_image_url: string;
  cover_image_alt: string;
  category_id: string;
  status: "draft" | "published" | "archived";
  author_name: string;
  seo_title: string;
  seo_description: string;
  focus_keyword: string;
  secondary_keywords: string; // comma-separated in the UI
  key_takeaways: string; // newline-separated in the UI
  entities: string; // comma-separated
  source_urls: string; // newline-separated
  faq: FaqItem[];
};

const EMPTY_FORM: FormState = {
  title: "",
  slug: "",
  subtitle: "",
  excerpt: "",
  content_md: "",
  cover_image_url: "",
  cover_image_alt: "",
  category_id: "",
  status: "draft",
  author_name: "Martyn Johnson",
  seo_title: "",
  seo_description: "",
  focus_keyword: "",
  secondary_keywords: "",
  key_takeaways: "",
  entities: "",
  source_urls: "",
  faq: [],
};

const csvToArray = (s: string) =>
  s.split(",").map((x) => x.trim()).filter(Boolean);
const linesToArray = (s: string) =>
  s.split("\n").map((x) => x.trim()).filter(Boolean);
const arrToCsv = (a: string[] | null | undefined) => (a ?? []).join(", ");
const arrToLines = (a: string[] | null | undefined) => (a ?? []).join("\n");

export function ArticleEditor({ id }: { id?: string }) {
  const navigate = useNavigate();
  const { data: categories } = useQuery({ queryKey: ["categories"], queryFn: () => listCategories() });
  const { data: authors } = useQuery({
    queryKey: ["authors", "public"],
    queryFn: () => listAuthorsPublic(),
  });
  const { data: existing } = useQuery({
    queryKey: ["admin", "article", id],
    queryFn: () => adminGetArticle({ data: { id: id! } }),
    enabled: !!id,
  });

  const [form, setForm] = useState<FormState>(EMPTY_FORM);

  useEffect(() => {
    if (existing) {
      setForm({
        title: existing.title ?? "",
        slug: existing.slug ?? "",
        subtitle: existing.subtitle ?? "",
        excerpt: existing.excerpt ?? "",
        content_md: existing.content_md ?? "",
        cover_image_url: existing.cover_image_url ?? "",
        cover_image_alt: existing.cover_image_alt ?? "",
        category_id: existing.category_id ?? "",
        status: (existing.status as any) ?? "draft",
        author_name: existing.author_name ?? "Martyn Johnson",
        seo_title: existing.seo_title ?? "",
        seo_description: existing.seo_description ?? "",
        focus_keyword: (existing as any).focus_keyword ?? "",
        secondary_keywords: arrToCsv((existing as any).secondary_keywords),
        key_takeaways: arrToLines((existing as any).key_takeaways),
        entities: arrToCsv((existing as any).entities),
        source_urls: arrToLines((existing as any).source_urls),
        faq: Array.isArray((existing as any).faq) ? (existing as any).faq : [],
      });
    }
  }, [existing]);

  const save = useMutation({
    mutationFn: () =>
      adminUpsertArticle({
        data: {
          id,
          title: form.title,
          slug: form.slug || undefined,
          subtitle: form.subtitle || null,
          excerpt: form.excerpt || null,
          content_md: form.content_md,
          cover_image_url: form.cover_image_url || null,
          cover_image_alt: form.cover_image_alt || null,
          category_id: form.category_id || null,
          status: form.status,
          author_name: form.author_name || null,
          seo_title: form.seo_title || null,
          seo_description: form.seo_description || null,
          focus_keyword: form.focus_keyword || null,
          secondary_keywords: csvToArray(form.secondary_keywords),
          key_takeaways: linesToArray(form.key_takeaways),
          entities: csvToArray(form.entities),
          source_urls: linesToArray(form.source_urls),
          faq: form.faq.filter((f) => f.question.trim() && f.answer.trim()),
        },
      }),
    onSuccess: (res) => {
      toast.success("Saved");
      if (!id) navigate({ to: "/admin/edit/$id", params: { id: res.id } });
    },
    onError: (e: any) => toast.error(e.message ?? "Failed to save"),
  });

  const publish = useMutation({
    mutationFn: async (opts: { force?: boolean } = {}) => {
      if (!id) throw new Error("Save the article first, then publish.");
      // Persist any pending edits before flipping the row live.
      await save.mutateAsync();
      return adminPublishArticle({ data: { id, force: opts.force } });
    },
    onSuccess: (res: any) => {
      if (res?.alreadyPublished) {
        toast.info("Already published");
      } else {
        toast.success(
          res?.syndicated ? "Published & syndicated" : "Published (syndication skipped)",
        );
      }
      if (res?.slug) navigate({ to: "/article/$slug", params: { slug: res.slug } });
    },
    onError: (e: any) => toast.error(e?.message ?? "Publish failed"),
  });

  const isPublished = (existing as any)?.status === "published";

  const handlePublish = (force = false) => {
    if (!id) {
      toast.error("Save the article first, then use Publish now.");
      return;
    }
    const warnings: string[] = [];
    if (!form.cover_image_url) warnings.push("• No cover image — one will be generated.");
    if (existing && !(existing as any).editor_pass_completed_at) {
      warnings.push("• No editor pass has been run — this is an operator override.");
    }
    const header = force
      ? `Re-publish "${form.title || "this article"}"?\n\nThis will re-run cover generation and syndication (Bluesky, Mastodon, IndexNow) without changing the original publish date.`
      : `Publish "${form.title || "this article"}" live now?\n\nThis will:\n• Ensure a cover image\n• Flip the row to Published\n• Trigger syndication (Bluesky, Mastodon, IndexNow)`;
    const msg = header + (warnings.length ? `\n\nNotes:\n${warnings.join("\n")}` : "");
    if (!window.confirm(msg)) return;
    publish.mutate({ force });
  };


  const input = "w-full border border-border bg-background px-3 py-2 text-sm";

  const applyGeneratedDraft = (draft: any) => {
    const category = categories?.find((c) => c.slug === draft.category_slug);
    setForm((prev) => ({
      ...prev,
      title: draft.title ?? prev.title,
      subtitle: draft.subtitle ?? "",
      excerpt: draft.excerpt ?? "",
      content_md: draft.content_md ?? "",
      cover_image_alt: draft.cover_image_alt ?? "",
      category_id: category?.id ?? prev.category_id,
      seo_title: draft.seo_title ?? "",
      seo_description: draft.seo_description ?? "",
      focus_keyword: draft.focus_keyword ?? "",
      secondary_keywords: arrToCsv(draft.secondary_keywords),
      key_takeaways: arrToLines(draft.key_takeaways),
      entities: arrToCsv(draft.entities),
      source_urls: arrToLines(draft.source_urls),
      faq: Array.isArray(draft.faq) ? draft.faq : [],
      author_name: prev.author_name || "Martyn Johnson",
    }));
  };

  return (
    <div className="space-y-6">
      {!id && <GenerateDraftPanel onDraft={applyGeneratedDraft} />}

      <form
        onSubmit={(e) => { e.preventDefault(); save.mutate(); }}
        className="grid gap-6 lg:grid-cols-[2fr_1fr]"
      >
        <div className="space-y-4">
          <div>
            <label className="text-xs uppercase tracking-wider text-muted-foreground">Title</label>
            <input className={input + " text-lg"} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-muted-foreground">Subtitle</label>
            <input className={input} value={form.subtitle} onChange={(e) => setForm({ ...form, subtitle: e.target.value })} />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-muted-foreground">Excerpt</label>
            <textarea rows={2} className={input} value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-muted-foreground">Body (Markdown)</label>
            <textarea rows={22} className={input + " font-mono"} value={form.content_md} onChange={(e) => setForm({ ...form, content_md: e.target.value })} />
          </div>

          <div>
            <label className="text-xs uppercase tracking-wider text-muted-foreground">Key takeaways (one per line)</label>
            <textarea rows={4} className={input} value={form.key_takeaways} onChange={(e) => setForm({ ...form, key_takeaways: e.target.value })} placeholder="Three to six standalone bullets." />
          </div>

          <FaqEditor faq={form.faq} onChange={(faq) => setForm({ ...form, faq })} />

          <div>
            <label className="text-xs uppercase tracking-wider text-muted-foreground">Source URLs (one per line)</label>
            <textarea rows={3} className={input + " font-mono"} value={form.source_urls} onChange={(e) => setForm({ ...form, source_urls: e.target.value })} placeholder="https://example.com/article" />
          </div>
        </div>

        <aside className="space-y-4 lg:sticky lg:top-4 lg:self-start">
          {id ? (
            <div className="border-2 border-foreground bg-background p-3 space-y-2">
              <p className="eyebrow">Actions</p>
              <button
                type="submit"
                disabled={save.isPending || publish.isPending}
                className="w-full bg-foreground text-background py-2.5 font-medium hover:bg-ochre-deep transition-colors disabled:opacity-50"
              >
                {save.isPending ? "Saving…" : "Save changes"}
              </button>
              {!existing ? (
                <button
                  type="button"
                  disabled
                  className="w-full border-2 border-foreground bg-background text-foreground py-2.5 font-medium opacity-50"
                >
                  Loading…
                </button>
              ) : isPublished ? (
                <button
                  type="button"
                  onClick={() => handlePublish(true)}
                  disabled={publish.isPending || save.isPending}
                  className="w-full border-2 border-foreground bg-background text-foreground py-2.5 font-medium hover:bg-foreground hover:text-background transition-colors disabled:opacity-50"
                >
                  {publish.isPending ? "Re-publishing…" : "Re-publish / re-syndicate"}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => handlePublish(false)}
                  disabled={publish.isPending || save.isPending}
                  className="w-full bg-ochre-deep text-background py-2.5 font-semibold hover:bg-foreground transition-colors disabled:opacity-50"
                >
                  {publish.isPending ? "Publishing…" : "Publish now"}
                </button>
              )}
              <p className="text-[11px] text-muted-foreground leading-snug">
                Status dropdown below is for demoting to draft/archived — not the primary publish action.
              </p>
            </div>
          ) : (
            <div className="border-2 border-foreground bg-background p-3 space-y-2">
              <p className="eyebrow">Actions</p>
              <button
                type="submit"
                disabled={save.isPending}
                className="w-full bg-foreground text-background py-2.5 font-medium hover:bg-ochre-deep transition-colors disabled:opacity-50"
              >
                {save.isPending ? "Creating…" : "Create article"}
              </button>
              <p className="text-[11px] text-muted-foreground leading-snug">
                Save the draft first, then <strong>Publish now</strong> will appear here.
              </p>
            </div>
          )}

          {id && existing && (
            <div className="border border-border bg-paper-soft/40 p-3 text-xs space-y-1">
              <div>
                <span className="text-muted-foreground">Status: </span>
                <span className="font-medium">{(existing as any).status}</span>
              </div>
              {(existing as any).published_at && (
                <div>
                  <span className="text-muted-foreground">Published: </span>
                  <span className="font-medium">{formatDate((existing as any).published_at)}</span>
                </div>
              )}
              {(existing as any).quality_score != null && (
                <div>
                  <span className="text-muted-foreground">Editor score: </span>
                  <span className="font-medium">{(existing as any).quality_score}/100</span>
                </div>
              )}
            </div>
          )}

          <div>
            <label className="text-xs uppercase tracking-wider text-muted-foreground">Status</label>
            <select className={input} value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value as any })}>
              <option value="draft">Draft</option>
              <option value="published">Published</option>
              <option value="archived">Archived</option>
            </select>
            <p className="mt-1 text-xs text-muted-foreground">
              Use this to demote to draft/archived. To publish live, use <strong>Publish now</strong> below — it runs the same cover + syndication pipeline as the automated newsroom.
            </p>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-muted-foreground">Category</label>
            <select className={input} value={form.category_id} onChange={(e) => setForm({ ...form, category_id: e.target.value })}>
              <option value="">— none —</option>
              {categories?.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-muted-foreground">Slug</label>
            <input className={input} value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} placeholder="auto from title" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-muted-foreground">Author</label>
            <select
              className={input}
              value={form.author_name}
              onChange={(e) => setForm({ ...form, author_name: e.target.value })}
            >
              {!authors?.some((a: any) => a.name === form.author_name) && form.author_name && (
                <option value={form.author_name}>{form.author_name} (not in registry)</option>
              )}
              {(authors ?? []).map((a: any) => (
                <option key={a.id} value={a.name}>
                  {a.name}
                  {a.is_default ? " (default)" : ""}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-muted-foreground">
              Manage bios and social links in <strong>Authors</strong>.
            </p>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-muted-foreground">Cover image URL</label>
            <input className={input} value={form.cover_image_url} onChange={(e) => setForm({ ...form, cover_image_url: e.target.value })} />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-muted-foreground">Cover alt</label>
            <input className={input} value={form.cover_image_alt} onChange={(e) => setForm({ ...form, cover_image_alt: e.target.value })} />
          </div>

          <div>
            <label className="text-xs uppercase tracking-wider text-muted-foreground">Focus keyword</label>
            <input className={input} value={form.focus_keyword} onChange={(e) => setForm({ ...form, focus_keyword: e.target.value })} />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-muted-foreground">Secondary keywords (comma-separated)</label>
            <input className={input} value={form.secondary_keywords} onChange={(e) => setForm({ ...form, secondary_keywords: e.target.value })} />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-muted-foreground">Entities (comma-separated)</label>
            <input className={input} value={form.entities} onChange={(e) => setForm({ ...form, entities: e.target.value })} />
          </div>

          <div>
            <label className="text-xs uppercase tracking-wider text-muted-foreground">SEO title</label>
            <input className={input} value={form.seo_title} onChange={(e) => setForm({ ...form, seo_title: e.target.value })} />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-muted-foreground">SEO description</label>
            <textarea rows={3} className={input} value={form.seo_description} onChange={(e) => setForm({ ...form, seo_description: e.target.value })} />
          </div>
          {id && existing && <ProvenancePanel article={existing as any} />}

        </aside>
      </form>
    </div>
  );
}

function FaqEditor({ faq, onChange }: { faq: FaqItem[]; onChange: (faq: FaqItem[]) => void }) {
  const input = "w-full border border-border bg-background px-3 py-2 text-sm";
  return (
    <div>
      <div className="flex items-center justify-between">
        <label className="text-xs uppercase tracking-wider text-muted-foreground">FAQ</label>
        <button
          type="button"
          onClick={() => onChange([...faq, { question: "", answer: "" }])}
          className="text-xs text-ochre-deep hover:underline"
        >
          + Add question
        </button>
      </div>
      <div className="space-y-3 mt-2">
        {faq.length === 0 && (
          <p className="text-xs text-muted-foreground italic">No FAQ items yet.</p>
        )}
        {faq.map((item, i) => (
          <div key={i} className="border border-border p-3 space-y-2">
            <input
              className={input}
              value={item.question}
              placeholder="Question"
              onChange={(e) => {
                const next = [...faq];
                next[i] = { ...next[i], question: e.target.value };
                onChange(next);
              }}
            />
            <textarea
              rows={3}
              className={input}
              value={item.answer}
              placeholder="Answer (40–120 words)"
              onChange={(e) => {
                const next = [...faq];
                next[i] = { ...next[i], answer: e.target.value };
                onChange(next);
              }}
            />
            <button
              type="button"
              onClick={() => onChange(faq.filter((_, j) => j !== i))}
              className="text-xs text-muted-foreground hover:text-destructive"
            >
              Remove
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function GenerateDraftPanel({ onDraft }: { onDraft: (draft: any) => void }) {
  const [mode, setMode] = useState<"url" | "text">("url");
  const [url, setUrl] = useState("");
  const [text, setText] = useState("");
  const [hint, setHint] = useState("");
  const [elapsed, setElapsed] = useState(0);

  const mutation = useMutation({
    mutationFn: async () => {
      return await adminGenerateDraftFromInput({
        data: {
          mode,
          url: mode === "url" ? url.trim() : null,
          text: mode === "text" ? text : null,
          hint: hint.trim() || null,
        },
      });
    },
    onSuccess: (draft) => {
      onDraft(draft);
      toast.success(
        draft.quality_score != null
          ? `Draft ready — editor score ${draft.quality_score}/100`
          : "Draft ready",
      );
    },
    onError: (e: any) => toast.error(e.message ?? "Generation failed"),
  });

  useEffect(() => {
    if (!mutation.isPending) {
      setElapsed(0);
      return;
    }
    const started = Date.now();
    const t = setInterval(() => setElapsed(Math.floor((Date.now() - started) / 1000)), 500);
    return () => clearInterval(t);
  }, [mutation.isPending]);

  const stage = elapsed < 15 ? "Scraping source" : elapsed < 60 ? "Drafting article" : "Editor pass";
  const input = "w-full border border-border bg-background px-3 py-2 text-sm";

  return (
    <div className="border border-border bg-paper-soft/40 p-4 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="eyebrow">Generate draft</p>
          <p className="text-sm text-muted-foreground mt-1">
            Point at a URL to scrape, or paste a transcript / notes. Runs the same writer + editor pipeline as the automated newsroom, then pre-fills the form below.
          </p>
        </div>
      </div>

      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => setMode("url")}
          className={`px-3 py-1.5 text-xs font-medium border ${mode === "url" ? "bg-foreground text-background border-foreground" : "border-border"}`}
        >
          From URL
        </button>
        <button
          type="button"
          onClick={() => setMode("text")}
          className={`px-3 py-1.5 text-xs font-medium border ${mode === "text" ? "bg-foreground text-background border-foreground" : "border-border"}`}
        >
          From text / transcript
        </button>
      </div>

      {mode === "url" ? (
        <div>
          <label className="text-xs uppercase tracking-wider text-muted-foreground">Source URL</label>
          <input
            className={input}
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://example.com/article"
          />
        </div>
      ) : (
        <div>
          <label className="text-xs uppercase tracking-wider text-muted-foreground">
            Source text (transcript, notes, briefing — min 200 chars)
          </label>
          <textarea
            rows={8}
            className={input + " font-mono"}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Paste a YouTube transcript, your own notes, a press release, or bullet points…"
          />
          <p className="mt-1 text-xs text-muted-foreground">{text.length.toLocaleString()} chars</p>
        </div>
      )}

      <div>
        <label className="text-xs uppercase tracking-wider text-muted-foreground">Editorial angle (optional)</label>
        <input
          className={input}
          value={hint}
          onChange={(e) => setHint(e.target.value)}
          placeholder="e.g. Explain what this means for multi-location operators."
        />
      </div>

      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => mutation.mutate()}
          disabled={mutation.isPending || (mode === "url" ? !url.trim() : text.trim().length < 200)}
          className="bg-foreground text-background px-4 py-2 text-sm font-medium hover:bg-ochre-deep transition-colors disabled:opacity-50"
        >
          {mutation.isPending ? `${stage}… ${elapsed}s` : "Generate draft"}
        </button>
        {mutation.isPending && (
          <p className="text-xs text-muted-foreground">
            Typical run is 45–90 seconds. Fields below will populate automatically.
          </p>
        )}
      </div>
    </div>
  );
}

function ProvenancePanel({ article }: { article: any }) {
  const idea = article.idea ?? null;
  const provenance = article.provenance ?? null;
  const sourceUrls: string[] = Array.isArray(article.source_urls) ? article.source_urls : [];
  const sourcePublishedAt: string | null =
    article.source_published_at ?? provenance?.source_published_at ?? idea?.source_published_at ?? null;
  const sourceName: string | null = provenance?.source_name ?? idea?.source_name ?? null;
  const sourceTier: string | null = article.source_tier ?? provenance?.source_tier ?? idea?.source_tier ?? null;
  const sourceChannel: string | null = provenance?.source_channel ?? idea?.source_channel ?? null;
  const noveltyScore = provenance?.novelty_score ?? idea?.novelty_score ?? null;
  const urgency = provenance?.urgency ?? idea?.urgency ?? null;
  const discoveredAt: string | null =
    provenance?.discovered_at ?? idea?.discovered_at ?? idea?.created_at ?? null;
  const discoveryJobId: string | null = article.discovery_job_id ?? null;
  const isBackfill = !!provenance?.is_backfill;

  const ageDays = useMemo(() => {
    if (!sourcePublishedAt) return null;
    return Math.round((Date.now() - new Date(sourcePublishedAt).getTime()) / 86_400_000);
  }, [sourcePublishedAt]);
  const isStale = ageDays != null && ageDays > 60;

  if (!idea && !provenance && sourceUrls.length === 0) return null;

  return (
    <div className="mt-4 border border-border bg-paper-soft/40 p-4 space-y-3 text-sm">
      <div className="flex items-center justify-between">
        <p className="eyebrow">Provenance</p>
        {isStale && (
          <span
            className="rounded-sm bg-amber-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-amber-900"
            title={`Source is ${ageDays} days old`}
          >
            Stale source · {ageDays}d
          </span>
        )}
      </div>

      {sourceUrls.length > 0 && (
        <div>
          <div className="text-xs uppercase tracking-wider text-muted-foreground">Source URL</div>
          <ul className="mt-1 space-y-1">
            {sourceUrls.map((u) => (
              <li key={u}>
                <a
                  href={u}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="break-all text-ochre-deep underline hover:text-accent"
                >
                  {u}
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}

      <dl className="grid grid-cols-2 gap-x-4 gap-y-2">
        {sourceName && (
          <>
            <dt className="text-xs uppercase tracking-wider text-muted-foreground">Source</dt>
            <dd>{sourceName}{sourceTier ? ` · tier ${sourceTier}` : ""}</dd>
          </>
        )}
        {sourceChannel && (
          <>
            <dt className="text-xs uppercase tracking-wider text-muted-foreground">Channel</dt>
            <dd>{sourceChannel}</dd>
          </>
        )}
        {sourcePublishedAt && (
          <>
            <dt className="text-xs uppercase tracking-wider text-muted-foreground">Source date</dt>
            <dd>{formatDate(sourcePublishedAt)}{ageDays != null ? ` · ${ageDays}d ago` : ""}</dd>
          </>
        )}
        {discoveredAt && (
          <>
            <dt className="text-xs uppercase tracking-wider text-muted-foreground">Discovered</dt>
            <dd>{formatDate(discoveredAt)}</dd>
          </>
        )}
        {noveltyScore != null && (
          <>
            <dt className="text-xs uppercase tracking-wider text-muted-foreground">Novelty</dt>
            <dd>{noveltyScore}/100{urgency ? ` · ${urgency}` : ""}</dd>
          </>
        )}
        {discoveryJobId && (
          <>
            <dt className="text-xs uppercase tracking-wider text-muted-foreground">Discovery job</dt>
            <dd className="font-mono text-xs break-all">{discoveryJobId}</dd>
          </>
        )}
        {isBackfill && (
          <>
            <dt className="text-xs uppercase tracking-wider text-muted-foreground">Mode</dt>
            <dd>Backfill</dd>
          </>
        )}
      </dl>

      {idea && (
        <div className="border-t border-border pt-3">
          <div className="text-xs uppercase tracking-wider text-muted-foreground">Original idea</div>
          <p className="mt-1 font-medium">{idea.title}</p>
          {idea.summary && <p className="mt-1 text-muted-foreground">{idea.summary}</p>}
          {idea.angle && (
            <p className="mt-1 text-xs italic text-muted-foreground">
              <span className="font-semibold not-italic">Angle:</span> {idea.angle}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
